import {HttpClient} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {REMIT_BASE_URL} from './config';
import {RemitApiResponse} from './kyc/user-verification.service';

export interface BankQueryParams {
  pageSize?: number;
  pageNumber?: number;
  search?: string;
  isHidden?: '' | 'Y' | 'N';
  swiftStatus?: '' | 'missing' | 'present';
  sortBy?: '_id' | 'bankName' | 'updatedAt';
  sortDirection?: 1 | -1;
}

export interface RemitBanks {
  body: RemitBank[];
  totalData: number;
}

export interface BankSnapshot {
  bankName: string;
  legalBankName?: string;
  swiftCode: string;
  thunesId?: string;
  isHidden?: 'Y' | 'N' | '';
}

export interface RemitBank extends BankSnapshot {
  bankCode: string;
  version?: string;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
  previousNames?: string[];
}

export interface BankChangeContext {
  kind: 'created' | 'correction' | 'renamed' | 'reassigned';
  reason: string;
  effectiveDate?: string;
  source?: string;
}

export interface BankRevision {
  id: string;
  recordedAt: string;
  recordedBy: string;
  before?: BankSnapshot;
  after: BankSnapshot;
  change?: BankChangeContext;
}

export interface BankHistory {
  body: BankRevision[];
  totalData: number;
}

export interface CreateBankRequest extends BankSnapshot {
  bankCode: string;
  changeReason?: string;
  source?: string;
}

export interface UpdateBankRequest extends Partial<BankSnapshot> {
  version: string;
  changeReason: string;
  changeType: 'correction' | 'renamed' | 'reassigned';
  effectiveDate?: string;
  source?: string;
  acknowledgeImpact?: boolean;
}

export interface BankResponse extends RemitApiResponse {
  body: RemitBank;
}

export interface BankSaveResponse extends RemitApiResponse {
  body?: RemitBank;
}

export interface SwiftCodes {
  body: SwiftCode[];
  totalData: number;
}

export interface SwiftCode {
  swiftCode: string;
  legalName: string;
  name: string;
}

@Injectable({providedIn: 'root'})
export class BankCodeService {
  constructor(
    private http: HttpClient,
    @Inject(REMIT_BASE_URL) private remitApiUrl: string,
  ) {}

  queryBanks(params: BankQueryParams): Observable<RemitBanks> {
    return this.http.post<RemitBanks>(
        this.remitApiUrl + '/staff/bankcodes',
        params,
    );
  }

  getBank(id: string): Observable<BankResponse> {
    return this.http.get<BankResponse>(
        this.remitApiUrl + `/staff/bankcode/${encodeURIComponent(id)}`,
    );
  }

  getHistory(id: string, pageNumber = 0): Observable<BankHistory> {
    return this.http.get<BankHistory>(
        this.remitApiUrl + `/staff/bankcode/${encodeURIComponent(id)}/history`,
        {params: {pageNumber}},
    );
  }

  createBank(bankInfo: CreateBankRequest): Observable<BankSaveResponse> {
    return this.http.post<BankSaveResponse>(
        this.remitApiUrl + '/staff/bankcode',
        bankInfo,
    );
  }

  updateBank(
      id: string,
      bankInfo: UpdateBankRequest,
  ): Observable<BankSaveResponse> {
    return this.http.post<BankSaveResponse>(
        this.remitApiUrl + `/staff/bankcode/${encodeURIComponent(id)}/revisions`,
        bankInfo,
    );
  }

  querySwiftCodes(): Observable<SwiftCodes> {
    return this.http.post<SwiftCodes>(
        this.remitApiUrl + '/staff/swift-codes',
        {},
    );
  }
}
